import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import UseItForm from './UseItForm';
import RecommendItForm from './RecommendItForm';
import { hasReachedTrialLimitAsync, getRemainingTrialCalls } from '../../services/trialLimitService';
import logo from '../../assets/images/LifeCoachAssist-logo-02.png';
import toast from 'react-hot-toast';

const { FiX } = FiIcons;

const SignupOptionsPage = () => {
  const [activeForm, setActiveForm] = useState(null);
  const [trialLimitReached, setTrialLimitReached] = useState(false);
  const [remainingTrials, setRemainingTrials] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const checkLimit = async () => {
      // Check trial limit on mount
      const limitReached = await hasReachedTrialLimitAsync();
      const remaining = await getRemainingTrialCalls();
      setTrialLimitReached(limitReached);
      setRemainingTrials(remaining);
    };
    
    checkLimit();
    
    // Check for form query parameter
    const formParam = searchParams.get('form');
    if (formParam === 'use-it') {
      setActiveForm('use-it');
    }
  }, [searchParams]);

  const handleCloseForm = () => {
    setActiveForm(null);
  };

  const handleTryIt = () => {
    if (trialLimitReached) {
      toast.error('Trial limit reached. Please sign up to continue!', {
        duration: 4000,
        icon: '🎯'
      });
      return;
    }
    navigate('/trial-call');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 mt-4 pt-safe">
      <div className="container mx-auto px-4 py-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img 
            src={logo} 
            alt="Life Coach Assist" 
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Learn More about Life Coach Assist
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Here are some options to explore our AI-powered coaching platform
          </p>
        </div>

        {/* Options Cards */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Test It Card (formerly Try It, moved to first position) */}
          <motion.div
            whileHover={!trialLimitReached ? { scale: 1.05 } : {}}
            whileTap={!trialLimitReached ? { scale: 0.98 } : {}}
            className={`bg-white rounded-2xl shadow-xl p-8 border-2 transition-all ${
              trialLimitReached
                ? 'border-gray-300 opacity-60 cursor-not-allowed'
                : 'cursor-pointer border-transparent hover:border-yellow-400'
            }`}
            onClick={handleTryIt}
          >
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Test It</h2>
              <p className="text-gray-600 mb-6">
                {trialLimitReached
                  ? 'Trial limit reached'
                  : 'This is how a client would experience always-available contact with the coach\'s AI assistant.'
                }
              </p>
              <button
                disabled={trialLimitReached}
                className={`w-full py-3 rounded-lg font-bold transition-colors ${
                  trialLimitReached
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-400 text-gray-900 hover:bg-yellow-500'
                }`}
              >
                {trialLimitReached ? 'Limit Reached' : 'Start Test'}
              </button>
            </div>
          </motion.div>

          {/* Explore It Card (new) */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white rounded-2xl shadow-xl p-8 cursor-pointer border-2 border-transparent hover:border-yellow-400 transition-all"
            onClick={() => window.open('https://www.lifecoachassist.ai', '_blank')}
          >
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Explore It</h2>
              <p className="text-gray-600 mb-6">
                Our website describes many ways this application enhances the coaching process and provides the client 24/7 service.
              </p>
              <button className="w-full bg-yellow-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors">
                Visit Website
              </button>
            </div>
          </motion.div>

          {/* Recommend It Card */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white rounded-2xl shadow-xl p-8 cursor-pointer border-2 border-transparent hover:border-yellow-400 transition-all"
            onClick={() => setActiveForm('recommend-it')}
          >
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Recommend It</h2>
              <p className="text-gray-600 mb-6">
                You can suggest to others to explore this application that cuts paperwork while improving coaching.
              </p>
              <button className="w-full bg-yellow-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors">
                Recommend
              </button>
            </div>
          </motion.div>

          {/* Use It Card */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white rounded-2xl shadow-xl p-8 cursor-pointer border-2 border-transparent hover:border-yellow-400 transition-all"
            onClick={() => setActiveForm('use-it')}
          >
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Use It</h2>
              <p className="text-gray-600 mb-6">
                As a life coach, you can apply to use this application for your clients.
              </p>
              <button className="w-full bg-yellow-400 text-gray-900 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors">
                Get Started
              </button>
            </div>
          </motion.div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center">
          <Link
            to="/auth"
            className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-2"
          >
            <span>←</span> Back to login if you are a registered user.
          </Link>
        </div>
      </div>

      {/* Form Modals */}
      <AnimatePresence>
        {activeForm === 'use-it' && (
          <UseItForm isOpen={true} onClose={handleCloseForm} />
        )}
        {activeForm === 'recommend-it' && (
          <RecommendItForm isOpen={true} onClose={handleCloseForm} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SignupOptionsPage;
